import React from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '../../components/PageLayout';
import PageHero from '../../components/PageHero';
import Button from '../../components/Button';

const BREADCRUMBS = [
  { label: 'Home', to: '/' },
  { label: 'Our Story', to: '/our-story/ifys-story' },
  { label: 'IFYS Story' },
];

const IfysStoryPage = () => {
  return (
    <PageLayout activeLink="our-story">
      <PageHero
        title="IFYS Story"
        backgroundImage="/images/thumb3.jpg"
        breadcrumbs={BREADCRUMBS}
        variant="split"
      />

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 md:py-20">
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-ifys-blue mb-6">
          Our Mission
        </h2>

        <p className="text-lg sm:text-xl font-semibold text-ifys-blue mb-8 leading-relaxed">
          To actively pursue opportunities that create change, develop awareness and strengthen the human spirit.
        </p>

        <div className="space-y-6 text-gray-700 leading-relaxed text-base sm:text-lg">
          <p>
            In 1980, in response to a growing need for a youth shelter on the Sunshine Coast, the
            Maroochydore Area Youth Services (MAYS) was founded. Operating from a small, converted
            house in Beach Road, Maroochydore, the fledging staff of one Co-ordinator and two Youth
            Workers managed a small, 4 bed emergency shelter that provided accommodation and youth
            related information and support to homeless, young people aged between 15-25 years.
          </p>

          <p>
            Constantly morphing and evolving over the ensuing years, the organisation recognised that
            its future lay in being able to provide a far broader range of services to its community
            than those provided in its original charter. Within this context the organisation
            re-evaluated its mission and goals, pragmatically embracing a broader mission statement
            more inclusive of families and those experiencing disabilities.
          </p>

          <p>
            In 2002, to better reflect this broader mission statement, the organisation changed its
            name to that which it has today - Integrated Family and Youth Service (IFYS). Integrated
            Family and Youth Service (IFYS) has now been servicing south-east, central and northern
            Queensland communities for over 30 plus years and during this time has instigated the
            development of a wide range of responses to the needs of children, young people and
            families.
          </p>

          <p>
            At our heart IFYS still delivers accommodation services to homeless and impoverished
            young people but, in addition to such, also provides counselling and mediation services;
            disability support, health, employment, education, family and parenting programs; foster
            care and flexible learning options. With services now stretching from the Gold Coast to
            the far north of the state. IFYS employs over 900 permanent and casual staff and is one
            of Queensland's largest non-church providers of beds to people, with over 1000 beds
            available throughout the state every night.
          </p>

          <p>
            Over the past four decades IFYS has gained a strong reputation as a provider of choice by
            delivering a wide range of programs across multiple regional areas to meet the needs of
            children, young people, and families. Our service delivery stretches from Sunshine Coast,
            where our organisation has its largest presence, to regional services in Cairns, Mackay,
            Rockhampton, Hervey Bay, Ipswich, Toowoomba, Brisbane, and the Gold Coast.
          </p>

          <p>
            IFYS is governed by a skilled and dedicated Board of Directors, which meets regularly,
            providing rigorous scrutiny and strategic direction. An experienced Executive Leadership
            Team with over 80 years of combined experience and Program Managers with the essential
            focus for helping and maintaining marginalised individuals who have a need for services
            through the day-to-day provision of services. In terms of organisational efficiencies,
            IFYS operates a shared services model with central business functions housed in the
            Maroochydore Corporate Office. This delivers centralised practice development, quality
            assurance, human resources, recruitment, finance, governance, marketing and other
            business functions, which are essential support elements to all our care services. This
            centralised model is a key example of IFYS' strength in size while maintaining person -
            centric service delivery to children, young people and families.
          </p>
        </div>

        <div className="mt-10">
          <Button
            href="/our-story/sanctuary"
            variant="primary"
            size="lg"
          >
            Learn More
          </Button>
        </div>
      </section>

      {/* Sub-page links */}
      <section className="bg-gray-50 border-t border-gray-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <h2 className="text-center text-ifys-blue text-2xl sm:text-3xl font-bold mb-10">
            Explore Our Story
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 max-w-3xl mx-auto">
            <Link
              to="/our-story/our-people"
              className="group relative block rounded-2xl overflow-hidden bg-white border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
            >
              <div className="px-8 py-10 flex flex-col items-center text-center gap-4">
                <div className="w-16 h-16 rounded-full bg-ifys-blue/10 flex items-center justify-center group-hover:bg-ifys-blue/20 transition-colors duration-300">
                  <svg className="w-8 h-8 text-ifys-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-ifys-blue">Our People</h3>
                <p className="text-sm text-gray-500 leading-relaxed">
                  Meet the dedicated team driving our mission forward.
                </p>
                <span className="inline-flex items-center gap-1 text-sm font-semibold text-ifys-blue group-hover:gap-2 transition-all duration-200">
                  Read more
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </span>
              </div>
            </Link>
            <Link
              to="/our-story/sanctuary"
              className="group relative block rounded-2xl overflow-hidden bg-white border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
            >
              <div className="px-8 py-10 flex flex-col items-center text-center gap-4">
                <div className="w-16 h-16 rounded-full bg-ifys-blue/10 flex items-center justify-center group-hover:bg-ifys-blue/20 transition-colors duration-300">
                  <svg className="w-8 h-8 text-ifys-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-ifys-blue">Sanctuary</h3>
                <p className="text-sm text-gray-500 leading-relaxed">
                  Learn about the IFYS Way and our culture of care.
                </p>
                <span className="inline-flex items-center gap-1 text-sm font-semibold text-ifys-blue group-hover:gap-2 transition-all duration-200">
                  Read more
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </span>
              </div>
            </Link>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default IfysStoryPage;
